import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday
} from "date-fns";
import {
  Calendar, LogOut, Clock, MapPin, DollarSign, CheckCircle, AlertCircle,
  User, Users, Plus, ChevronLeft, ChevronRight, LayoutGrid, List as ListIcon,
  Camera, X, Phone, LayoutDashboard, FileText, Search, Scissors
} from "lucide-react";
import { PhotoThumbs } from "@/components/PhotoThumbs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressMatchSuggestion from "@/components/AddressMatchSuggestion";
import ViewToggle from "@/components/ViewToggle";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const statusColor = (status) => {
  switch (status) {
    case "completed": return "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30";
    case "in_progress": return "bg-yellow-400/20 text-yellow-400 border-yellow-400/30";
    case "cancelled": return "bg-red-400/20 text-red-400 border-red-400/30";
    default: return "bg-blue-400/20 text-blue-400 border-blue-400/30";
  }
};

const StaffSchedule = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calView, setCalView] = useState("grid"); // grid | list
  const [selectedDay, setSelectedDay] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, custRes] = await Promise.all([
        axios.get(`${API}/jobs`, { withCredentials: true }),
        axios.get(`${API}/customers`, { withCredentials: true }),
      ]);
      setJobs(jobsRes.data);
      setCustomers(custRes.data);
    } catch (error) {
      console.error("Failed to fetch staff data:", error);
      toast.error("Failed to load schedule data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, { status: newStatus }, { withCredentials: true });
      toast.success("Job status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const jobsForMonth = useMemo(() => jobs.filter((j) => {
    try { return isSameMonth(parseISO(j.scheduled_date), currentMonth); } catch { return false; }
  }), [jobs, currentMonth]);

  const jobsByDate = useMemo(() => {
    const map = {};
    jobsForMonth.forEach((j) => {
      try {
        const key = format(parseISO(j.scheduled_date), "yyyy-MM-dd");
        (map[key] = map[key] || []).push(j);
      } catch { /* skip */ }
    });
    return map;
  }, [jobsForMonth]);

  // Calendar grid days
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    const days = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [currentMonth]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAdmin && <ViewToggle />}
            <img src={LOGO_URL} alt="Zig Zag Lawncare" className="h-12 w-auto object-contain rounded-lg" />
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3">
              <Avatar className="w-8 h-8 border-2 border-[#39FF14]">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#064E3B] text-[#39FF14] text-sm">{user?.name?.charAt(0) || "U"}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white text-sm font-medium">{user?.name}</p>
                <p className="text-[#39FF14] text-xs capitalize">{isAdmin ? "Admin (Staff View)" : "Staff"}</p>
              </div>
            </div>
            <Button data-testid="staff-logout-btn" variant="outline" size="sm"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-white" onClick={logout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase">Staff Portal</h1>
            <p className="text-[#A1A1AA]">View all jobs, customers & log completed work</p>
          </div>
          <Button data-testid="open-log-job-btn" onClick={() => setLogOpen(true)}
            variant="outline"
            className="border-[#27272A] text-white hover:bg-[#27272A]">
            <Plus className="w-4 h-4 mr-2" /> Detailed Log
          </Button>
        </div>

        {/* Quick Log a Cut */}
        <QuickLogCut customers={customers} onLogged={fetchData} />

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <Tabs defaultValue="calendar" className="w-full">
            <TabsList className="bg-[#121212] border border-[#27272A]">
              <TabsTrigger value="calendar" data-testid="tab-calendar" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                <Calendar className="w-4 h-4 mr-2" /> Jobs Calendar
              </TabsTrigger>
              <TabsTrigger value="customers" data-testid="tab-customers" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                <Users className="w-4 h-4 mr-2" /> Customers ({customers.length})
              </TabsTrigger>
              <TabsTrigger value="cutreport" data-testid="tab-cutreport" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                <FileText className="w-4 h-4 mr-2" /> Cut Report
              </TabsTrigger>
            </TabsList>

            {/* CALENDAR TAB */}
            <TabsContent value="calendar" className="mt-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Button data-testid="prev-month-btn" variant="outline" size="icon"
                    className="border-[#27272A] text-white hover:bg-[#27272A]" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-white font-heading text-lg uppercase min-w-[180px] text-center">{format(currentMonth, "MMMM yyyy")}</span>
                  <Button data-testid="next-month-btn" variant="outline" size="icon"
                    className="border-[#27272A] text-white hover:bg-[#27272A]" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="cal-grid-btn" variant={calView === "grid" ? "default" : "outline"} size="sm"
                    className={calView === "grid" ? "bg-[#39FF14] text-black" : "border-[#27272A] text-white hover:bg-[#27272A]"}
                    onClick={() => setCalView("grid")}>
                    <LayoutGrid className="w-4 h-4 mr-1" /> Grid
                  </Button>
                  <Button data-testid="cal-list-btn" variant={calView === "list" ? "default" : "outline"} size="sm"
                    className={calView === "list" ? "bg-[#39FF14] text-black" : "border-[#27272A] text-white hover:bg-[#27272A]"}
                    onClick={() => setCalView("list")}>
                    <ListIcon className="w-4 h-4 mr-1" /> List
                  </Button>
                </div>
              </div>

              {calView === "grid" ? (
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-3">
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                      <div key={d} className="text-center text-[#A1A1AA] text-xs uppercase font-semibold py-1">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day) => {
                      const key = format(day, "yyyy-MM-dd");
                      const dayJobs = jobsByDate[key] || [];
                      const inMonth = isSameMonth(day, currentMonth);
                      return (
                        <button
                          key={key}
                          data-testid={`cal-day-${key}`}
                          onClick={() => dayJobs.length && setSelectedDay(key)}
                          className={`min-h-[70px] rounded-lg p-1.5 text-left border transition-colors ${
                            inMonth ? "bg-[#0A0A0A] border-[#27272A]" : "bg-transparent border-transparent opacity-40"
                          } ${dayJobs.length ? "hover:border-[#39FF14]/50 cursor-pointer" : "cursor-default"} ${isToday(day) ? "ring-1 ring-[#39FF14]" : ""}`}
                        >
                          <span className={`text-xs ${isToday(day) ? "text-[#39FF14] font-bold" : "text-white"}`}>{format(day, "d")}</span>
                          <div className="mt-1 space-y-0.5">
                            {dayJobs.slice(0, 2).map((j) => (
                              <div key={j.job_id} className={`text-[10px] truncate px-1 py-0.5 rounded ${statusColor(j.status)}`}>
                                {j.customer_name}
                              </div>
                            ))}
                            {dayJobs.length > 2 && <div className="text-[10px] text-[#A1A1AA]">+{dayJobs.length - 2} more</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.keys(jobsByDate).sort().length === 0 ? (
                    <EmptyState icon={Calendar} title="No jobs this month" />
                  ) : (
                    Object.keys(jobsByDate).sort().map((key) => (
                      <div key={key} className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
                        <p className="text-[#39FF14] font-semibold mb-3">{format(parseISO(key), "EEEE, MMMM d")}</p>
                        <div className="space-y-3">
                          {jobsByDate[key].map((j) => <JobRow key={j.job_id} job={j} onStatus={handleStatusChange} />)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </TabsContent>

            {/* CUSTOMERS TAB */}
            <TabsContent value="customers" className="mt-6">
              {customers.length === 0 ? (
                <EmptyState icon={Users} title="No customers found" />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {customers.map((c) => (
                    <div key={c.customer_id} data-testid={`staff-customer-${c.customer_id}`}
                      className="bg-[#121212] border border-[#27272A] rounded-xl p-4 hover:border-[#39FF14]/30 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-white font-semibold">
                          <User className="w-4 h-4 text-[#39FF14]" /> {c.name}
                        </div>
                        {c.subscription_plan && c.subscription_plan !== "none" && (
                          <Badge className="bg-[#064E3B] text-[#39FF14] border-[#39FF14]/30 capitalize">{c.subscription_plan}</Badge>
                        )}
                      </div>
                      {c.address && <div className="flex items-start gap-2 text-[#A1A1AA] text-sm mb-1"><MapPin className="w-4 h-4 mt-0.5 text-[#39FF14]" /> {c.address}</div>}
                      {c.phone && <div className="flex items-center gap-2 text-[#A1A1AA] text-sm"><Phone className="w-4 h-4 text-[#39FF14]" /> {c.phone}</div>}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* CUT REPORT TAB */}
            <TabsContent value="cutreport" className="mt-6">
              <StaffCutReport />
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Day detail dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedDay && format(parseISO(selectedDay), "EEEE, MMMM d, yyyy")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedDay && (jobsByDate[selectedDay] || []).map((j) => <JobRow key={j.job_id} job={j} onStatus={handleStatusChange} />)}
          </div>
        </DialogContent>
      </Dialog>

      <LogJobDialog open={logOpen} onOpenChange={setLogOpen} customers={customers} onLogged={() => { setLogOpen(false); fetchData(); }} />
    </div>
  );
};

const EmptyState = ({ icon: Icon, title }) => (
  <div className="text-center py-16">
    <Icon className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
    <h3 className="text-xl text-white">{title}</h3>
  </div>
);

const StaffCutReport = () => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [monthly, setMonthly] = useState(null);
  const [view, setView] = useState("summary");

  useEffect(() => {
    axios.get(`${API}/admin/cut-report`, { withCredentials: true })
      .then((r) => setSummary(r.data || []))
      .catch(() => toast.error("Failed to load cut report"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/cut-report/monthly`, { params: { month }, withCredentials: true })
      .then((r) => setMonthly(r.data))
      .catch(() => setMonthly({ count: 0, total_amount: 0, cuts: [] }));
  }, [month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(format(new Date(y, m - 1 + delta, 1), "yyyy-MM"));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button data-testid="cr-summary-btn" size="sm" variant={view === "summary" ? "default" : "outline"}
          className={view === "summary" ? "bg-[#39FF14] text-black" : "border-[#27272A] text-white hover:bg-[#27272A]"}
          onClick={() => setView("summary")}>Last Cut Summary</Button>
        <Button data-testid="cr-monthly-btn" size="sm" variant={view === "monthly" ? "default" : "outline"}
          className={view === "monthly" ? "bg-[#39FF14] text-black" : "border-[#27272A] text-white hover:bg-[#27272A]"}
          onClick={() => setView("monthly")}>Monthly Cuts</Button>
      </div>

      {view === "summary" ? (
        loading ? (
          <div className="text-center py-12"><div className="w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : summary.length === 0 ? (
          <EmptyState icon={FileText} title="No active customers yet" />
        ) : (
          <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#27272A]">
                  <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Customer</th>
                  <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Address</th>
                  <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Last Cut</th>
                  <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Days Ago</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((c, i) => (
                  <tr key={`${c.customer_id}-${i}`} data-testid={`cr-row-${i}`} className="border-b border-[#27272A]/50 hover:bg-[#39FF14]/5">
                    <td className="px-4 py-3 text-white text-sm font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-[#A1A1AA] text-sm">{c.address || "—"}</td>
                    <td className="px-4 py-3 text-white text-sm">{c.last_cut_date ? format(parseISO(c.last_cut_date), "MMM d, yyyy") : "No cuts yet"}</td>
                    <td className="px-4 py-3 text-sm">
                      {c.days_since === null ? <span className="text-[#52525B]">—</span> :
                        <span className={c.days_since > 14 ? "text-red-400 font-semibold" : "text-[#39FF14]"}>{c.days_since}d</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button data-testid="cr-month-prev" variant="outline" size="icon" className="border-[#27272A] text-white hover:bg-[#27272A]" onClick={() => shiftMonth(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <input data-testid="cr-month-picker" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="bg-[#121212] border border-[#27272A] text-white rounded-md px-3 py-2 text-sm" />
              <Button data-testid="cr-month-next" variant="outline" size="icon" className="border-[#27272A] text-white hover:bg-[#27272A]" onClick={() => shiftMonth(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {monthly && (
              <div className="flex items-center gap-3 text-sm">
                <span className="bg-[#39FF14]/10 text-[#39FF14] px-3 py-1.5 rounded-full font-semibold">{monthly.count} cuts</span>
                <span className="bg-[#FACC15]/10 text-[#FACC15] px-3 py-1.5 rounded-full font-semibold flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{(monthly.total_amount || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
          <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
            {!monthly || monthly.cuts.length === 0 ? (
              <EmptyState icon={Calendar} title="No cuts completed this month" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#27272A]">
                    <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Customer</th>
                    <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Address</th>
                    <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Date</th>
                    <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Amount</th>
                    <th className="text-left px-4 py-3 text-[#A1A1AA] text-xs uppercase font-bold">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.cuts.map((cut, idx) => (
                    <tr key={`${cut.customer_id}-${idx}`} data-testid={`cr-monthly-row-${idx}`} className="border-b border-[#27272A]/50 hover:bg-[#39FF14]/5">
                      <td className="px-4 py-3 text-white text-sm font-medium">{cut.customer_name}</td>
                      <td className="px-4 py-3 text-[#A1A1AA] text-sm">{cut.address || "—"}</td>
                      <td className="px-4 py-3 text-white text-sm">{format(parseISO(cut.date), "MMM d")}</td>
                      <td className="px-4 py-3 text-[#39FF14] text-sm font-semibold">${(cut.amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cut.payment_status === "paid" ? "bg-[#39FF14]/10 text-[#39FF14]" : "bg-red-500/10 text-red-400"}`}>
                          {cut.payment_status === "paid" ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const JobRow = ({ job, onStatus }) => (
  <div data-testid={`staff-job-${job.job_id}`} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 text-white font-semibold"><User className="w-4 h-4 text-[#39FF14]" /> {job.customer_name}</div>
        {job.customer_address && <div className="flex items-start gap-2 text-[#A1A1AA] text-sm mt-1"><MapPin className="w-4 h-4 mt-0.5 text-[#39FF14]" /> {job.customer_address}</div>}
        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mt-1"><Clock className="w-4 h-4" /> {job.scheduled_time === "00:00" && job.status === "completed" ? "Logged" : job.scheduled_time}</div>
      </div>
      <div className="text-right">
        <div className="flex items-center justify-end gap-1 text-[#39FF14] font-bold"><DollarSign className="w-4 h-4" />{(job.final_price || 0).toFixed(2)}</div>
        {job.payment_status && (
          <Badge className={job.payment_status === "paid" ? "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30 mt-1" : "bg-red-400/20 text-red-400 border-red-400/30 mt-1"}>
            {job.payment_status === "paid" ? "Paid" : "Unpaid"}
          </Badge>
        )}
      </div>
    </div>
    <PhotoThumbs photos={job.photos} testIdPrefix={`staff-job-photo-${job.job_id}`} className="mt-3" />
    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#27272A]">
      <Badge className={statusColor(job.status)}>{job.status.replace("_", " ")}</Badge>
      <Select value={job.status} onValueChange={(v) => onStatus(job.job_id, v)}>
        <SelectTrigger data-testid={`job-status-select-${job.job_id}`} className="w-36 bg-[#121212] border-[#27272A] text-white h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#121212] border-[#27272A]">
          <SelectItem value="scheduled" className="text-white">Scheduled</SelectItem>
          <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
          <SelectItem value="completed" className="text-white">Completed</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

const QuickLogCut = ({ customers, onLogged }) => {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [amount, setAmount] = useState("");
  const [paid, setPaid] = useState(false);
  const [nextVisit, setNextVisit] = useState("none");
  const [photos, setPhotos] = useState([]);
  const [logging, setLogging] = useState(false);
  const debounceRef = useRef(null);

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    try {
      const urls = await Promise.all(files.map(fileToDataUrl));
      setPhotos((prev) => [...prev, ...urls]);
    } catch { toast.error("Failed to read photo"); }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 1) { setMatches([]); return; }
    debounceRef.current = setTimeout(async () => {
      // If it has a digit, use address match; otherwise filter customers by name
      if (/\d/.test(q)) {
        try {
          const res = await axios.get(`${API}/customers/match-address`, { params: { address: q }, withCredentials: true });
          setMatches(res.data || []);
        } catch { setMatches([]); }
      } else {
        const ql = q.toLowerCase();
        setMatches(customers.filter((c) => (c.name || "").toLowerCase().includes(ql)).slice(0, 5)
          .map((c) => ({ customer_id: c.customer_id, name: c.name, address: c.address })));
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, customers]);

  const logCut = async ({ customer_id, name, address }) => {
    setLogging(true);
    try {
      const res = await axios.post(`${API}/staff/jobs/log-completed`, {
        customer_id: customer_id || null,
        customer_name: name || query.trim(),
        customer_address: address || (customer_id ? "" : query.trim()),
        payment_status: paid ? "paid" : "unpaid",
        payment_amount: parseFloat(amount) || 0,
        photos,
        next_visit: nextVisit,
      }, { withCredentials: true });
      loggedToast(name || query.trim(), res);
      setQuery(""); setMatches([]); setAmount(""); setPaid(false); setPhotos([]); setNextVisit("none");
      onLogged && onLogged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log cut");
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="bg-[#121212] border border-[#39FF14]/30 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Scissors className="w-5 h-5 text-[#39FF14]" />
        <h2 className="text-white font-heading text-lg uppercase">Quick Log a Cut</h2>
      </div>
      <p className="text-[#A1A1AA] text-xs mb-3">Type a house number (e.g. 5437) or name, tap the customer, and the cut is logged to today's schedule.</p>
      <div className="flex flex-wrap gap-2 items-stretch">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="quick-log-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="House number or name..."
            className="bg-[#0A0A0A] border-[#27272A] text-white pl-9 h-11 text-base"
          />
        </div>
        <div className="relative w-28">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39FF14] font-bold">$</span>
          <Input
            data-testid="quick-log-amount"
            type="number" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amt" className="bg-[#0A0A0A] border-[#27272A] text-white pl-7 h-11" />
        </div>
        <Button
          type="button"
          data-testid="quick-log-paid-toggle"
          onClick={() => setPaid(!paid)}
          className={`h-11 ${paid ? "bg-[#39FF14] text-black" : "bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]"}`}
        >
          {paid ? "Paid" : "Unpaid"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center mt-2">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Calendar className="w-4 h-4 text-[#39FF14] shrink-0" />
          <NextVisitSelect value={nextVisit} onChange={setNextVisit} testId="quick-log-next-visit" className="h-11 flex-1" />
        </div>
        <label htmlFor="quick-log-photo-input" data-testid="quick-log-photo-btn"
          className="h-11 px-4 flex items-center gap-2 border border-dashed border-[#27272A] rounded-md text-[#A1A1AA] text-sm cursor-pointer hover:border-[#39FF14]/50 hover:text-white transition-colors">
          <Camera className="w-4 h-4" /> {photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""}` : "Add photo"}
        </label>
        <input id="quick-log-photo-input" data-testid="quick-log-photo-input" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handlePhotos} />
      </div>
      {photos.length > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap" data-testid="quick-log-photo-previews">
          {photos.map((p, i) => (
            <div key={i} className="relative">
              <img src={p} alt="cut" className="w-14 h-14 object-cover rounded-lg border border-[#27272A]" />
              <button type="button" data-testid={`quick-log-photo-remove-${i}`} onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"><X className="w-3 h-3 text-white" /></button>
            </div>
          ))}
        </div>
      )}

      {query.trim().length >= 1 && (
        <div className="mt-3 border border-[#27272A] rounded-lg divide-y divide-[#27272A] overflow-hidden">
          {matches.map((m) => (
            <button
              key={m.customer_id}
              type="button"
              data-testid={`quick-log-match-${m.customer_id}`}
              disabled={logging}
              onClick={() => logCut(m)}
              className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[#39FF14]/10 disabled:opacity-50"
            >
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{m.name}</div>
                {m.address && <div className="text-[#A1A1AA] text-xs truncate">{m.address}</div>}
              </div>
              <span className="shrink-0 flex items-center gap-1 bg-[#39FF14] text-black text-xs font-bold px-3 py-1.5 rounded-full">
                <CheckCircle className="w-3.5 h-3.5" /> Log Cut
              </span>
            </button>
          ))}
          {/* Allow logging as a new/ad-hoc entry */}
          <button
            type="button"
            data-testid="quick-log-new"
            disabled={logging}
            onClick={() => logCut({ customer_id: null, name: query.trim(), address: query.trim() })}
            className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[#27272A] disabled:opacity-50"
          >
            <span className="text-[#A1A1AA] text-sm">Log "<span className="text-white">{query.trim()}</span>" as a new / walk-in cut</span>
            <span className="shrink-0 text-[#39FF14] text-xs font-bold">+ New</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Read an image and shrink it (max 1280px, JPEG) to keep uploads light
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => resolve(reader.result);
    img.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.75));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const NEXT_VISIT_OPTIONS = [
  { value: "none", label: "No next cut" },
  { value: "1w", label: "In 1 week" },
  { value: "2w", label: "In 2 weeks" },
  { value: "1m", label: "In 1 month" },
  { value: "2m", label: "In 2 months" },
];

const NextVisitSelect = ({ value, onChange, testId, className = "" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`bg-[#0A0A0A] border-[#27272A] text-white ${className}`}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-[#121212] border-[#27272A]">
      {NEXT_VISIT_OPTIONS.map((o) => (
        <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`} className="text-white">{o.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const loggedToast = (name, res) => {
  const next = res?.data?.next_job;
  toast.success(next
    ? `Cut logged for ${name}. Next cut booked for ${format(parseISO(next.scheduled_date.slice(0, 10)), "EEE, MMM d")}`
    : `Cut logged for ${name}`);
};

const LogJobDialog = ({ open, onOpenChange, customers, onLogged }) => {
  const [mode, setMode] = useState("existing"); // existing | new
  const [customerId, setCustomerId] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [paid, setPaid] = useState("unpaid");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [nextVisit, setNextVisit] = useState("none");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMode("existing"); setCustomerId(""); setName(""); setAddress("");
    setDate(format(new Date(), "yyyy-MM-dd")); setPaid("unpaid"); setAmount(""); setNotes(""); setPhotos([]); setNextVisit("none");
  };

  const handleSelectCustomer = (id) => {
    setCustomerId(id);
    const c = customers.find((x) => x.customer_id === id);
    if (c) { setName(c.name); setAddress(c.address || ""); }
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    try {
      const urls = await Promise.all(files.map(fileToDataUrl));
      setPhotos((prev) => [...prev, ...urls]);
    } catch { toast.error("Failed to read photo"); }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Customer name is required"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/staff/jobs/log-completed`, {
        customer_id: mode === "existing" ? customerId || null : null,
        customer_name: name.trim(),
        customer_address: address.trim(),
        scheduled_date: date,
        payment_status: paid,
        payment_amount: parseFloat(amount) || 0,
        notes: notes.trim() || null,
        photos,
        next_visit: nextVisit,
      }, { withCredentials: true });
      loggedToast(name.trim(), res);
      reset();
      onLogged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#39FF14]" /> Log Completed Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <Button type="button" size="sm" data-testid="mode-existing-btn"
              variant={mode === "existing" ? "default" : "outline"}
              className={mode === "existing" ? "bg-[#39FF14] text-black flex-1" : "border-[#27272A] text-white flex-1"}
              onClick={() => { setMode("existing"); setName(""); setAddress(""); }}>
              Existing Customer
            </Button>
            <Button type="button" size="sm" data-testid="mode-new-btn"
              variant={mode === "new" ? "default" : "outline"}
              className={mode === "new" ? "bg-[#39FF14] text-black flex-1" : "border-[#27272A] text-white flex-1"}
              onClick={() => { setMode("new"); setCustomerId(""); setName(""); setAddress(""); }}>
              New / Walk-in
            </Button>
          </div>

          {mode === "existing" && (
            <div>
              <Label className="text-[#A1A1AA]">Select Customer</Label>
              <Select value={customerId} onValueChange={handleSelectCustomer}>
                <SelectTrigger data-testid="log-customer-select" className="bg-[#0A0A0A] border-[#27272A] text-white mt-1">
                  <SelectValue placeholder="Choose a customer" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] max-h-60">
                  {customers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id} className="text-white">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-[#A1A1AA]">Customer Name</Label>
            <Input data-testid="log-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Customer name" className="bg-[#0A0A0A] border-[#27272A] text-white mt-1" />
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Location / Address</Label>
            <Input data-testid="log-address-input" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Job location" className="bg-[#0A0A0A] border-[#27272A] text-white mt-1" />
            <AddressMatchSuggestion
              address={address}
              onConfirm={(c) => {
                setMode("existing");
                setCustomerId(c.customer_id);
                setName(c.name);
                setAddress(c.address || address);
                toast.success(`Linked to ${c.name}`);
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#A1A1AA]">Date</Label>
              <Input data-testid="log-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="bg-[#0A0A0A] border-[#27272A] text-white mt-1" />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Amount ($)</Label>
              <Input data-testid="log-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00" className="bg-[#0A0A0A] border-[#27272A] text-white mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Payment Status</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" size="sm" data-testid="log-paid-btn"
                variant={paid === "paid" ? "default" : "outline"}
                className={paid === "paid" ? "bg-[#39FF14] text-black flex-1" : "border-[#27272A] text-white flex-1"}
                onClick={() => setPaid("paid")}>Paid</Button>
              <Button type="button" size="sm" data-testid="log-unpaid-btn"
                variant={paid === "unpaid" ? "default" : "outline"}
                className={paid === "unpaid" ? "bg-red-500 text-white flex-1" : "border-[#27272A] text-white flex-1"}
                onClick={() => setPaid("unpaid")}>Unpaid</Button>
            </div>
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Photos</Label>
            <label htmlFor="job-photo-input" className="mt-1 flex items-center justify-center gap-2 border border-dashed border-[#27272A] rounded-lg py-3 text-[#A1A1AA] cursor-pointer hover:border-[#39FF14]/50">
              <Camera className="w-4 h-4" /> Take / Upload Photo
            </label>
            <input id="job-photo-input" data-testid="log-photo-input" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handlePhotos} />
            {photos.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="upload" className="w-16 h-16 object-cover rounded-lg border border-[#27272A]" />
                    <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                      className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Book Next Cut</Label>
            <NextVisitSelect value={nextVisit} onChange={setNextVisit} testId="log-next-visit" className="mt-1" />
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Notes</Label>
            <Textarea data-testid="log-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes" className="bg-[#0A0A0A] border-[#27272A] text-white mt-1" />
          </div>
          <Button data-testid="submit-log-job-btn" onClick={submit} disabled={submitting}
            className="w-full bg-[#39FF14] text-black hover:bg-[#32d912] font-semibold">
            {submitting ? "Saving..." : "Save Completed Job"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaffSchedule;
